import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown } from "lucide-react";
import { LANGUAGES_SIMPLE } from "@/lib/languages";
import { useSignIn } from "@/hooks/useSignIn";

// Supported languages - matching what's shown in My Content tab article settings
const POPULAR_LANGUAGE_CODES = [
  // Major European Languages
  'en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'pl', 'sv', 'no', 'da', 'fi', 'cs', 'hu', 'ro', 'bg', 'hr', 'sk', 'sl', 'tr', 'uk', 'el', 'lt', 'lv', 'et', 'mk', 'sq', 'sr', 'bs', 'me', 'ca', 'eu', 'gl', 'cy', 'ga', 'gd', 'mt', 'is', 'fo', 'lb',
  // Asian Languages
  'ja', 'ko', 'zh', 'zh-tw', 'hi', 'bn', 'ur', 'ta', 'te', 'ml', 'kn', 'gu', 'pa', 'th', 'vi', 'id', 'ms', 'tl', 'my', 'km', 'lo',
  // Middle Eastern & African Languages
  'ar', 'fa', 'he', 'am', 'sw', 'ha', 'yo', 'ig', 'zu', 'af', 'xh',
  // Americas Regional Variants
  'pt-br', 'es-mx', 'es-ar', 'fr-ca'
];

// Get only the supported languages (excluding 'ru' and 'be')
const SUPPORTED_LANGUAGES = LANGUAGES_SIMPLE
  .filter(l => POPULAR_LANGUAGE_CODES.includes(l.value))
  .filter(l => l.value !== 'ru' && l.value !== 'be');

interface FAQProps {
  onSignIn: () => void;
}

const FAQ: React.FC<FAQProps> = ({ onSignIn }) => {
  const { signingIn } = useSignIn();
  
  const handleClick = async () => {
    if (signingIn) return;
    await onSignIn();
  };
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const [showLanguages, setShowLanguages] = React.useState(false);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
    if (openIndex === index) {
      setShowLanguages(false);
    }
  };

  const faqs = [
    {
      question: "Do you provide invoices for business purchases?",
      answer: "Yes! When you subscribe and select that you're purchasing as a business, fill out your business information during checkout. After successful payment, you'll instantly receive an invoice via email. The same applies to all subscription renewals."
    },
    {
      question: "How does the content generation work?",
      answer: "Our AI analyzes your website to create topic clusters and article ideas. You get 1 full analysis per month. From there, you can generate up to 30 high-quality, SEO-optimized articles monthly that align with your niche."
    },
    {
      question: "What are the usage limits?",
      answer: "Your subscription includes 30 article generations, 10 premium backlinks, and 1 comprehensive website analysis per month. Limits reset each billing cycle."
    },
    {
      question: "How do the backlinks work?",
      answer: "You receive 10 premium backlinks (DA 70+) every month. You can redeem these directly from the Backlinks tab in your dashboard to boost your domain authority."
    },
    {
      question: "What integrations do you support?",
      answer: "We seamlessly integrate with Shopify, WordPress, and Webflow. Framer integration is coming soon! You can publish your generated articles directly to your CMS with a single click."
    },
    {
      question: "Does it support other languages?",
      answer: `Yes! We support ${SUPPORTED_LANGUAGES.length} languages. Our AI can generate high-quality content in any language you need, making it perfect for international businesses or multi-language websites.`,
      showLanguages: true
    },
    {
      question: "What is the LLM.txt feature?",
      answer: "The LLM.txt feature generates a specialized file that helps Large Language Models (like ChatGPT, Claude) better understand and index your website content, improving your visibility in AI search results."
    },
    {
      question: "Can I review articles before publication?",
      answer: "Yes! You have full control over the content. You can review, edit, and regenerate any article before it gets published to your site."
    }
  ];

  return (
    <section id="faq" className="w-full py-16 sm:py-20 lg:py-24 scroll-mt-24">
      {/* FAQPage Schema for Rich Snippets */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })
        }}
      />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              Have Questions?
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8">
              If you can't find what you're looking for, feel free to reach out!
            </p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4 mb-16">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-background rounded-2xl border border-border/50 shadow-sm hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 rounded-2xl transition-colors"
                >
                  <h3 className="text-lg font-semibold text-foreground pr-4">
                    {faq.question}
                  </h3>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform duration-200 flex-shrink-0 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4">
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      {faq.answer}
                    </p>
                    {faq.showLanguages && (
                      <div className="mt-4 pt-4 border-t border-border/50">
                        <button
                          onClick={() => setShowLanguages(!showLanguages)}
                          className="w-full text-left text-sm font-medium text-foreground hover:text-primary transition-colors flex items-center gap-2 mb-3"
                        >
                          <ChevronDown className={`h-4 w-4 transition-transform ${showLanguages ? 'rotate-180' : ''}`} />
                          <span>View all supported languages</span>
                        </button>
                        {showLanguages && (
                          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-96 overflow-y-auto pr-2">
                            {SUPPORTED_LANGUAGES.map((lang) => (
                              <div
                                key={lang.value}
                                className="flex items-center gap-1.5 text-xs text-muted-foreground py-1"
                              >
                                <span className="text-sm">{lang.flag}</span>
                                <span className="truncate">{lang.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-3xl p-8 sm:p-10 border border-primary/20">
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Let's Try!
              </h3>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Start ranking higher and growing your organic traffic today. Join thousands of entrepreneurs who trust our platform to scale their SEO efforts.
              </p>
              <Button
                onClick={handleClick}
                disabled={signingIn}
                className="w-full sm:w-auto rounded-full text-lg px-8 py-6 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg min-h-[60px] relative overflow-hidden group mx-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
                size="lg"
                className="w-full sm:w-auto rounded-full text-lg px-8 py-6 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl min-h-[60px] relative overflow-hidden group mx-auto"
              >
                <span className="relative z-10 font-semibold tracking-wide">Get Started Now</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
